<!--
  Хедер лендинга — плавающая стеклянная капсула по центру экрана. Раньше он
  жил разметкой внутри HeroSection.vue; теперь это отдельный компонент, роутер
  подставляет его в route.meta.header (см. router/index.js).

  Десктоп: лого слева, меню по центру, вход и кнопка справа.
  Мобила: то же стекло, но свёрнутое до лого и бургера — по тапу капсула
  раскрывается вниз, меню и кнопка встают столбиком внутри того же стекла.
-->
<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import Glass from '@martyrs/src/design-system/components/Glass/Glass.vue';

import { openApplication } from '@/store/application.js';

const menu_items = [
  { label_key: 'header.howItWorks', href: '#how-it-works' },
  { label_key: 'header.places', href: '#places' },
  { label_key: 'header.packages', href: '#plans' },
];

const is_open = ref(false);
const header = ref(null);

const toggleMenu = () => {
  is_open.value = !is_open.value;
};

const closeMenu = () => {
  is_open.value = false;
};

// Клик мимо капсулы и Escape закрывают меню: раскрытая капсула перекрывает
// верх страницы, и без этого её нечем убрать, кроме повторного тапа по бургеру.
const closeOnOutsideClick = (event) => {
  if (!header.value || header.value.contains(event.target)) return;
  closeMenu();
};

const closeOnEscape = (event) => {
  if (event.key !== 'Escape') return;
  closeMenu();
};

onMounted(() => {
  document.addEventListener('click', closeOnOutsideClick);
  document.addEventListener('keydown', closeOnEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeOnOutsideClick);
  document.removeEventListener('keydown', closeOnEscape);
});
</script>

<template>
  <div class="dobro-header" ref="header">
    <div :class="['dobro-header-capsule', { 'dobro-header-capsule-open': is_open }]">
      <!-- Слои «жидкого стекла» дизайн-системы. Хост держит position: relative
           и свой радиус — слои тянут inset: 0 и radius: inherit. -->
      <Glass />

      <div class="dobro-header-bar">
        <!-- Боковые зоны одной ширины: меню между ними встаёт ровно по центру
             капсулы, хотя лого и правый блок разной ширины. -->
        <div class="dobro-header-side dobro-header-side-start">
          <a class="dobro-header-logo" href="#top" :aria-label="$t('header.home')" @click="closeMenu">
            <img src="/images/logo.webp" alt="Dobroverse">
          </a>
        </div>

        <nav class="dobro-header-nav" :aria-label="$t('header.nav')">
          <a v-for="item in menu_items" :key="item.href" :href="item.href">{{ $t(item.label_key) }}</a>
        </nav>

        <div class="dobro-header-side dobro-header-side-end">
          <button class="button button--accent button--l dobro-header-button" type="button" @click="openApplication()">{{ $t('header.action') }}</button>
        </div>

        <button
          class="dobro-header-burger"
          type="button"
          :aria-expanded="is_open"
          aria-controls="dobro-header-drawer"
          :aria-label="$t('header.menu')"
          @click="toggleMenu"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </button>
      </div>

      <div class="dobro-header-drawer" id="dobro-header-drawer" v-show="is_open">
        <nav :aria-label="$t('header.nav')">
          <a v-for="item in menu_items" :key="item.href" :href="item.href" @click="closeMenu">{{ $t(item.label_key) }}</a>
        </nav>

        <button class="button button--accent button--l" type="button" @click="closeMenu(); openApplication()">{{ $t('header.action') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dobro-header {
  position: fixed;
  /* Обёртка страницы в martyrs Client.vue лежит на z-index: 10 и в DOM идёт
     после хедера — на равном слое она закрывала бы капсулу. */
  z-index: 20;
  top: var(--regular);
  right: 0;
  left: 0;
  display: flex;
  justify-content: center;
  padding: 0 var(--extra);
  pointer-events: none;
}

/* Капсула ловит указатель сама: полоса вокруг неё сквозная, иначе она
   перекрывала бы верх страницы по всей ширине. */
.dobro-header-capsule {
  position: relative;
  width: max-content;
  max-width: 100%;
  border-radius: var(--radius-pill);
  padding: var(--thin);
  pointer-events: auto;
}

.dobro-header-bar {
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--medium);
}

.dobro-header-side {
  display: flex;
  align-items: center;
  gap: var(--medium);
  min-width: 14rem;
  flex-shrink: 0;
}

.dobro-header-side-start {
  justify-content: flex-start;
}

.dobro-header-side-end {
  justify-content: flex-end;
}

.dobro-header-logo {
  display: block;
  width: 9.5rem;
  padding-left: var(--small);
}

.dobro-header-logo img {
  display: block;
  width: 100%;
}

.dobro-header-nav {
  display: flex;
  align-items: center;
  gap: var(--semi);
}

.dobro-header-nav a,
.dobro-header-drawer a {
  color: rgb(var(--dobro-ink));
  font-family: var(--font-main);
  font-size: 0.82rem;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
}

.dobro-header-button {
  min-width: 10.5rem;
  border-radius: var(--radius-pill);
  text-transform: uppercase;
}

/* Бургер — три полосы, в раскрытом состоянии складываются в крест.
   Геометрия иконки живёт в двух переменных: шаг между полосами и их толщина.
   Ими же считается сдвиг полос в крест — иначе крест разъезжается, как только
   меняется зазор. Шаг взят от ширины полосы (22px), а не из шкалы отступов:
   на 8px три коротких штриха читались как знак «равно». */
.dobro-header-burger {
  --burger-bar: 0.15rem;
  --burger-gap: 0.3rem;
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: var(--burger-gap);
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 auto;
  align-items: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.dobro-header-burger span {
  display: block;
  width: 1.375rem;
  height: var(--burger-bar);
  border-radius: var(--radius-pill);
  background: rgb(var(--dobro-ink));
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.dobro-header-capsule-open .dobro-header-burger span:first-child {
  transform: translateY(calc(var(--burger-gap) + var(--burger-bar))) rotate(45deg);
}

.dobro-header-capsule-open .dobro-header-burger span:nth-child(2) {
  opacity: 0;
}

.dobro-header-capsule-open .dobro-header-burger span:last-child {
  transform: translateY(calc((var(--burger-gap) + var(--burger-bar)) * -1)) rotate(-45deg);
}

.dobro-header-drawer {
  display: none;
}

/* Планшет: капсула остаётся десктопной, полоса вокруг неё уже. */
@media (min-width: 48.0625rem) and (max-width: 64rem) {
  .dobro-header {
    padding: 0 var(--medium);
  }

  .dobro-header-side {
    min-width: 10.5rem;
  }

  .dobro-header-nav {
    gap: var(--medium);
  }
}

@media (max-width: 48rem) {
  .dobro-header {
    padding: 0 var(--regular);
  }

  /* Свёрнутая капсула — на всю ширину полосы: лого слева, бургер справа. */
  .dobro-header-capsule {
    width: 100%;
    border-radius: var(--radius-pill);
  }

  /* Раскрытая капсула перестаёт быть пилюлей: столбик меню в pill-радиусе
     обрезался бы по краям. */
  .dobro-header-capsule-open {
    border-radius: 1.75rem;
  }

  .dobro-header-nav,
  .dobro-header-side-end {
    display: none;
  }

  .dobro-header-side {
    min-width: 0;
  }

  .dobro-header-burger {
    display: flex;
  }

  .dobro-header-drawer {
    position: relative;
    z-index: 5;
    display: flex;
    flex-direction: column;
    gap: var(--regular);
    padding: var(--regular) var(--small) var(--small);
  }

  .dobro-header-drawer nav {
    display: flex;
    flex-direction: column;
    gap: var(--regular);
    text-align: center;
  }

  .dobro-header-drawer > a {
    justify-content: center;
    border-radius: var(--radius-pill);
    text-transform: uppercase;
  }
}
</style>
